import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { InlineNotification } from '@carbon/react';

import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { InterestSourcePill } from 'components/interest-source-pill';
import { Link } from 'components/link';
import TooltipDecimal from 'components/tooltip-decimal/tooltip-decimal';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import type {
  DraftSlotsQueryParams,
  DraftSlotsResponse,
  Step2SlotRow,
} from '../types';
import {
  draftSlotFilterStateToParams,
  mapDraftSlotsToStep2SlotRows,
  WELL_STATUS_OPTIONS,
} from '../utils';
import styles from './step2-slot-details-table.module.scss';

/** Props for {@link Step2SlotDetailsTable}. */
export interface Step2SlotDetailsTableProps {
  /** Active draft id. The fetch fires on mount/draftId-change. */
  draftId: string;
}

/**
 * Read-only slot-grain query for Step 2's "Slot Details" review table.
 * Hits the same draft-slots endpoint Step 1 + Slot Allocations use, but
 * with its own abort controller — so paging Step 1 can't cancel Step 2
 * in-flight (and vice versa) — and intentionally skips the
 * `currentDraftSummaryAtom` write to keep Step 1's summary panel stable.
 */
const [, draftSlotDetailsQueryLoadableAtom] = mutationAtomWithLoadable<
  DraftSlotsResponse,
  [DraftSlotsQueryParams]
>(
  (() => {
    let controller: AbortController | null = null;
    return async (_get, _set, params: DraftSlotsQueryParams) => {
      controller?.abort();
      controller = new AbortController();
      const { draftId, ...body } = params;
      const url = `/queries/packages/drafts/${draftId}/slots`;
      const response = await request.post<
        DraftSlotsResponse,
        { errors: string[] }
      >(url, body, controller);
      if (response.type === 'success') return response.data;
      if (response.type === 'abort') return new Promise<never>(() => {});
      if (response.type === 'error' && response.data?.errors?.length) {
        throw new Error(response.data.errors.join(', '));
      }
      throw new Error('Failed to fetch draft slots');
    };
  })()
);

// Filter ids match the BE permits handled by `draftSlotFilterStateToParams`
// — only the BE-supported text/enum dimensions get a filter UI; the rest
// disable the column filter slot. Hole direction would be filterable on
// the BE but the inline filter UI for it isn't wired here yet.
const COLUMNS: DataTableColumnDef<Step2SlotRow>[] = [
  {
    id: 'slotId',
    accessorKey: 'slotId',
    header: 'Slot ID',
    meta: { type: 'text' as const },
    // UWI rows link to `/wells/:uwi`; "Slot Ref" rows (no uwi) fall back to
    // the development area via `unitHref`. The displayed value is the same
    // either way (`uwi ?? slotId` per the mapper); only the link target
    // changes. Renders inert when no link target is resolvable.
    cell: ({ row, getValue }) => {
      const display = getValue<string>();
      const { uwi, unitHref } = row.original;
      const to = uwi ? `/wells/${encodeURIComponent(uwi)}` : unitHref;
      if (!to) return display;
      return (
        <Link to={to} target="_blank" rel="noopener noreferrer">
          {display}
        </Link>
      );
    },
  },
  {
    id: 'unitName',
    accessorKey: 'unitName',
    header: 'Unit',
    meta: { type: 'text' as const },
    cell: ({ row, getValue }) => {
      const display = getValue<string | null>();
      if (!display) return '—';
      const { unitHref } = row.original;
      if (!unitHref) return display;
      return (
        <Link to={unitHref} target="_blank" rel="noopener noreferrer">
          {display}
        </Link>
      );
    },
  },
  {
    id: 'totalSlotNri',
    accessorKey: 'totalSlotNri',
    header: 'Total Slot NRI',
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <TooltipDecimal value={getValue<number | null>()} precision={8} />
        <InterestSourcePill wiSource={row.original.wiSource} />
      </span>
    ),
  },
  {
    id: 'totalSlotWi',
    accessorKey: 'totalSlotWi',
    header: 'Total Slot WI',
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <TooltipDecimal value={getValue<number | null>()} precision={8} />
        <InterestSourcePill wiSource={row.original.wiSource} />
      </span>
    ),
  },
  {
    id: 'wiVerified',
    accessorKey: 'wiVerified',
    header: 'WI Verified',
    enableColumnFilter: false,
  },
  {
    id: 'interestType',
    accessorKey: 'interestType',
    header: 'Interest Type',
    enableColumnFilter: false,
  },
  {
    id: 'unitSource',
    accessorKey: 'unitSource',
    header: 'Unit Source',
    enableColumnFilter: false,
  },
  {
    id: 'unitDevelopmentType',
    accessorKey: 'unitDevelopmentType',
    header: 'Unit Development Type',
    enableColumnFilter: false,
  },
  {
    id: 'wellName',
    accessorKey: 'wellName',
    header: 'Well Name',
    enableColumnFilter: false,
  },
  {
    id: 'wellStatus',
    accessorKey: 'wellStatus',
    header: 'Well Status',
    meta: { type: 'enum' as const, filterOptions: WELL_STATUS_OPTIONS },
  },
  {
    id: 'typeCurveStatus',
    accessorKey: 'typeCurveStatus',
    header: 'Type Curve Status',
    enableColumnFilter: false,
  },
  {
    id: 'operator',
    accessorKey: 'operator',
    header: 'Operator',
    meta: { type: 'text' as const },
  },
  {
    id: 'holeDirection',
    accessorKey: 'holeDirection',
    header: 'Hole Direction',
    enableColumnFilter: false,
  },
  {
    id: 'formation',
    accessorKey: 'formation',
    header: 'Formation',
    meta: { type: 'text' as const },
  },
  {
    id: 'slotNumber',
    accessorKey: 'slotNumber',
    header: 'Slot #',
    meta: { type: 'integer' as const },
    enableColumnFilter: false,
  },
  {
    id: 'permitDate',
    accessorKey: 'permitDate',
    header: 'Permit Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'spudDate',
    accessorKey: 'spudDate',
    header: 'Spud Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'rigReleaseDate',
    accessorKey: 'rigReleaseDate',
    header: 'Rig Release Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'completionDate',
    accessorKey: 'completionDate',
    header: 'Completion Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'firstProductionDate',
    accessorKey: 'firstProductionDate',
    header: 'First Production Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
];

/**
 * Step 2 read-only "Slot Details" table — slot-grain (one row per
 * draft slot, no tract fanout). Adapter-fed from `DraftSlot[]` via
 * {@link mapDraftSlotsToStep2SlotRows}, which mirrors the canonical
 * `mapSlotNRIResponseToSlotNRI` pattern used elsewhere in the app.
 *
 * NRI/WI cells render `<TooltipDecimal precision={8} />` for parity with
 * the Slot Allocations table. Verification rollup icons + Calculation /
 * NRI Matrix popovers are deferred until BE emits a per-slot verification
 * rollup (today only per-tract subtract verifications are exposed on
 * `DraftTract.interestBreakdown`).
 *
 * @param {Step2SlotDetailsTableProps} props - Draft id
 * @return {JSX.Element} The slot-details table
 */
export const Step2SlotDetailsTable: FC<Step2SlotDetailsTableProps> = ({
  draftId,
}) => {
  const [queryState, fetchSlots] = useAtom(draftSlotDetailsQueryLoadableAtom);

  const loadData = useCallback(
    (params: TableState) => {
      fetchSlots({
        draftId,
        // `selected: true` is the read-only contract — baked in so callers
        // can't accidentally widen the scope.
        selected: true,
        page: params.page,
        pageSize: params.pageSize,
        text: params.globalFilter || undefined,
        ...draftSlotFilterStateToParams(params.columnFilters),
      });
    },
    [draftId, fetchSlots]
  );

  // DataTable doesn't self-fire `loadData` on mount in paginated mode;
  // kick off the initial fetch when the draft id changes.
  useEffect(() => {
    loadData({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      globalFilter: '',
      sorting: [],
      columnFilters: [],
      selection: { selectAll: false, toggledNodes: [] },
      expanded: {},
    });
  }, [draftId, loadData]);

  const data = queryState.state === 'hasData' ? queryState.data : null;
  const rows = useMemo<Step2SlotRow[]>(
    () => (data ? mapDraftSlotsToStep2SlotRows(data.slots) : []),
    [data]
  );
  const totalCount = data?.meta.totalCount ?? 0;
  const isLoading = queryState.state === 'loading';

  const errorMessage: string | null =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'An unexpected error occurred'
      : null;

  return (
    <div className={styles.container}>
      {errorMessage !== null && (
        <InlineNotification
          kind="error"
          title="Slot details error"
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}
      <div className={styles.tableWrap}>
        <DataTable<Step2SlotRow>
          data={rows}
          columns={COLUMNS}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={loadData}
          onSortChange={loadData}
          onFilterChange={loadData}
          onGlobalSearchChange={loadData}
          config={{
            globalSearch: true,
            columnVisibility: true,
            columnFilters: true,
            // Match the BE-driven exporters' `draft-<id>-<table>.csv` shape so
            // a user's Downloads folder has consistent siblings:
            //   draft-<id>-assets.csv, draft-<id>-slots.csv,
            //   draft-<id>-slot_allocations.csv
            export: {
              exportPresets: [
                {
                  name: 'Export',
                  format: 'csv',
                  filename: `draft-${draftId}-slots`,
                },
              ],
            },
            sorting: false,
          }}
          searchPlaceholder="Search by Slot ID or Well Name"
        />
      </div>
    </div>
  );
};
